import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearToken, getToken, API } from "@/lib/api";
import { LogOut, Calendar, CheckCircle2, XCircle, Clock, User, Phone, Mail, FileText, Pin, Trash2, UserX, Download, AlertTriangle, Star, MessageSquareHeart, Ticket, BookOpen, Package, Plus, Minus, Save, Coins, Loader2, Lock } from "lucide-react";
import { AttachmentThumb } from "@/components/AttachmentThumb";
import { AdminUserAgenda } from "@/components/AdminUserAgenda";
import { AdminWaitlist } from "@/components/AdminWaitlist";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bookings");
  const [news, setNews] = useState([]);
  const [newNews, setNewNews] = useState({ title: "", body: "", pinned: false });
  const [noShowList, setNoShowList] = useState([]);
  const [feedbackData, setFeedbackData] = useState({ feedback: [], total: 0, avg_rating: 0 });
  const [ticketToday, setTicketToday] = useState(null);
  const [kbEntries, setKbEntries] = useState([]);
  const [newKb, setNewKb] = useState({ question: "", answer: "", keywords: "", category: "generale" });
  const [packages, setPackages] = useState([]);
  const [newPkg, setNewPkg] = useState({ name: "", price_eur: 5, labor_points: 100, active: true, order: 10 });
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState(null); // null=hidden, obj=editing
  const [adminBookForm, setAdminBookForm] = useState(null); // null=hidden, obj=booking
  const [newUserForm, setNewUserForm] = useState(null); // admin create user
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [calendarSelectedDay, setCalendarSelectedDay] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [closures, setClosures] = useState([]);
  const [newClosure, setNewClosure] = useState({ date: "", reason: "" });
  const [kbZipFile, setKbZipFile] = useState(null);
  const [kbZipUploading, setKbZipUploading] = useState(false);
  const [userDocs, setUserDocs] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [kbSelected, setKbSelected] = useState(new Set());
  const [docRequests, setDocRequests] = useState([]);
  const [docReqCounts, setDocReqCounts] = useState({});
  const [docReqFilter, setDocReqFilter] = useState("all");
  const [reqAdminNotes, setReqAdminNotes] = useState({});
  const [fallbacks, setFallbacks] = useState([]);
  const [fallbackCounts, setFallbackCounts] = useState({});
  const [fallbackFilter, setFallbackFilter] = useState("all");
  const [fallbackNotes, setFallbackNotes] = useState({});
  const [adminPwForm, setAdminPwForm] = useState({ old_password: "", new_password: "", new_password2: "" });
  const [adminPwSaving, setAdminPwSaving] = useState(false);
  const [adminPwMsg, setAdminPwMsg] = useState(null);

  useEffect(() => {
    if (!getToken()) { nav("/admin"); return; }
    const u = localStorage.getItem("labor_user");
    if (u) setUser(JSON.parse(u));
    loadBookings();
    loadNews();
    loadNoShowStats();
    loadFeedback();
    loadTickets();
    loadKb();
    loadPackages();
    loadUsers();
    loadRevenue();
    loadClosures();
    loadDocRequests();
    loadFallbacks();
  }, []);

  const loadTickets = async () => {
    try {
      const r = await api.get("/admin/urgency-tickets");
      setTicketToday(r.data.today);
    } catch {}
  };

  const updateTicket = async (patch) => {
    await api.patch("/admin/urgency-tickets/today", patch);
    loadTickets();
  };

  const loadKb = async () => {
    try { const r = await api.get("/admin/kb"); setKbEntries(r.data.entries || []); } catch {}
  };

  const addKb = async () => {
    if (!newKb.question || !newKb.answer) return;
    await api.post("/admin/kb", newKb);
    setNewKb({ question: "", answer: "", keywords: "", category: "generale" });
    loadKb();
  };

  const deleteKb = async (id) => {
    if (!confirm("Eliminare questa voce?")) return;
    await api.delete(`/admin/kb/${id}`);
    loadKb();
  };

  const loadPackages = async () => {
    try { const r = await api.get("/admin/packages"); setPackages(r.data.packages || []); } catch {}
  };

  const addPackage = async () => {
    if (!newPkg.name || !newPkg.price_eur || !newPkg.labor_points) return;
    await api.post("/admin/packages", newPkg);
    setNewPkg({ name: "", price_eur: 5, labor_points: 100, active: true, order: 10 });
    loadPackages();
  };

  const updatePackage = async (pkg) => {
    await api.patch(`/admin/packages/${pkg.id}`, pkg);
    loadPackages();
  };

  const deletePackage = async (id) => {
    if (!confirm("Eliminare questo pacchetto?")) return;
    await api.delete(`/admin/packages/${id}`);
    loadPackages();
  };

  const loadUsers = async (search = "") => {
    try {
      const r = await api.get("/admin/users", { params: search ? { search } : {} });
      setUsers(r.data.users || []);
    } catch {}
  };

  const loadRevenue = async () => {
    try { const r = await api.get("/admin/revenue"); setRevenue(r.data); } catch {}
  };

  const loadClosures = async () => {
    try { const r = await api.get("/office-closures"); setClosures(r.data.closures || []); } catch {}
  };

  const addClosure = async () => {
    if (!newClosure.date) { alert("Seleziona una data"); return; }
    try {
      const r = await api.post("/admin/office-closures", newClosure);
      alert(`Chiusura registrata. ${r.data.cancelled_bookings} appuntamenti annullati automaticamente.`);
      setNewClosure({ date: "", reason: "" });
      loadClosures();
      loadBookings();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const removeClosure = async (id) => {
    if (!confirm("Rimuovere questa chiusura?")) return;
    await api.delete(`/admin/office-closures/${id}`);
    loadClosures();
  };

  const uploadKbZip = async () => {
    if (!kbZipFile) return;
    setKbZipUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", kbZipFile);
      const r = await api.post("/admin/kb/import-zip", fd, { headers: { "Content-Type": "multipart/form-data" }});
      alert(`✅ Import completato!\nFile processati: ${r.data.files_processed}\nVoci KB create: ${r.data.entries_imported}`);
      setKbZipFile(null);
      loadKb();
    } catch (e) { alert(e.response?.data?.detail || "Errore import"); }
    finally { setKbZipUploading(false); }
  };

  const toggleKbSelected = (id) => {
    const n = new Set(kbSelected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setKbSelected(n);
  };

  const toggleKbSelectAll = () => {
    if (kbSelected.size === kbEntries.length) setKbSelected(new Set());
    else setKbSelected(new Set(kbEntries.map((e) => e.id)));
  };

  const bulkDeleteKb = async () => {
    if (kbSelected.size === 0) return;
    if (!confirm(`Eliminare le ${kbSelected.size} voci selezionate dalla Knowledge Base?`)) return;
    try {
      const r = await api.post("/admin/kb/bulk-delete", { ids: Array.from(kbSelected) });
      alert(`Eliminate ${r.data.deleted_count} voci`);
      setKbSelected(new Set());
      loadKb();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const deleteAllKb = async () => {
    if (!confirm(`⚠️ ATTENZIONE: stai per eliminare TUTTE le ${kbEntries.length} voci della Knowledge Base.\n\nL'operazione è irreversibile. Continuare?`)) return;
    const typed = prompt('Digita "ELIMINA TUTTO" (in maiuscolo) per confermare:');
    if (typed !== "ELIMINA TUTTO") { alert("Cancellazione annullata."); return; }
    try {
      const r = await api.delete("/admin/kb?confirm=YES");
      alert(`Eliminate ${r.data.deleted_count} voci`);
      setKbSelected(new Set());
      loadKb();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const loadDocRequests = async () => {
    try {
      const r = await api.get("/admin/document-requests");
      setDocRequests(r.data.requests || []);
      setDocReqCounts(r.data.counts || {});
    } catch {}
  };

  const updateDocRequest = async (reqId, status) => {
    try {
      await api.patch(`/admin/document-requests/${reqId}`, {
        status,
        admin_note: reqAdminNotes[reqId] || "",
      });
      loadDocRequests();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const deleteDocRequest = async (reqId) => {
    if (!confirm("Eliminare questa richiesta?")) return;
    try {
      await api.delete(`/admin/document-requests/${reqId}`);
      loadDocRequests();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const loadFallbacks = async () => {
    try {
      const r = await api.get("/admin/fallback-alerts");
      setFallbacks(r.data.alerts || []);
      setFallbackCounts(r.data.counts || {});
    } catch {}
  };

  const updateFallback = async (alertId, status) => {
    try {
      await api.patch(`/admin/fallback-alerts/${alertId}`, { status, resolution_note: fallbackNotes[alertId] || "" });
      loadFallbacks();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const deleteFallback = async (alertId) => {
    if (!confirm("Eliminare questa segnalazione?")) return;
    try {
      await api.delete(`/admin/fallback-alerts/${alertId}`);
      loadFallbacks();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const handleAdminChangePassword = async () => {
    setAdminPwMsg(null);
    if (!adminPwForm.old_password || !adminPwForm.new_password) { setAdminPwMsg({ type: "err", text: "Compila entrambi i campi" }); return; }
    if (adminPwForm.new_password !== adminPwForm.new_password2) { setAdminPwMsg({ type: "err", text: "Le due nuove password non coincidono" }); return; }
    if (adminPwForm.new_password.length < 8) { setAdminPwMsg({ type: "err", text: "Minimo 8 caratteri" }); return; }
    if (!/[A-Z]/.test(adminPwForm.new_password)) { setAdminPwMsg({ type: "err", text: "Serve almeno una lettera maiuscola" }); return; }
    if (!/[0-9]/.test(adminPwForm.new_password)) { setAdminPwMsg({ type: "err", text: "Serve almeno un numero" }); return; }
    setAdminPwSaving(true);
    try {
      await api.post("/admin/change-password", { old_password: adminPwForm.old_password, new_password: adminPwForm.new_password });
      setAdminPwForm({ old_password: "", new_password: "", new_password2: "" });
      setAdminPwMsg({ type: "ok", text: "✓ Password admin aggiornata" });
    } catch (e) {
      setAdminPwMsg({ type: "err", text: e.response?.data?.detail || "Errore" });
    } finally { setAdminPwSaving(false); }
  };

  const openUserDetail = async (id) => {
    try {
      const r = await api.get(`/admin/users/${id}`);
      setSelectedUser(r.data);
      loadUserDocs(id);
    } catch {}
  };

  const loadUserDocs = async (userId) => {
    try {
      const r = await api.get(`/admin/users/${userId}/documents`);
      setUserDocs(r.data.documents || []);
    } catch { setUserDocs([]); }
  };

  const uploadUserDoc = async () => {
    if (!docFile || !selectedUser) return;
    if (docFile.size > 4 * 1024 * 1024) { alert("File troppo grande (max 4MB)"); return; }
    if (docFile.type !== "application/pdf" && !docFile.name.toLowerCase().endsWith(".pdf")) {
      alert("Solo file PDF"); return;
    }
    setDocUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(docFile);
      });
      await api.post(`/admin/users/${selectedUser.user.id}/documents`, {
        filename: docFile.name,
        content_base64: b64,
        expires_days: 3,
      });
      setDocFile(null);
      loadUserDocs(selectedUser.user.id);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore upload PDF");
    } finally { setDocUploading(false); }
  };

  const deleteUserDoc = async (docId) => {
    if (!confirm("Eliminare questo documento?")) return;
    try {
      await api.delete(`/admin/documents/${docId}`);
      if (selectedUser) loadUserDocs(selectedUser.user.id);
    } catch (e) { alert("Errore eliminazione"); }
  };

  const downloadAdminDoc = async (docId, filename) => {
    try {
      const res = await fetch(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Errore download"); }
  };

  const adjustLP = async (amount, reason) => {
    if (!selectedUser) return;
    try {
      await api.post(`/admin/users/${selectedUser.user.id}/adjust-lp`, { amount, reason });
      // Refresh detail + users list
      await openUserDetail(selectedUser.user.id);
      loadUsers(userSearch);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore ricarica");
    }
  };

  const toggleBlock = async () => {
    if (!selectedUser) return;
    const isBlocked = selectedUser.user.blocked;
    const action = isBlocked ? "sbloccare" : "bloccare";
    let reason = "";
    if (!isBlocked) {
      reason = prompt(`Motivo del blocco (es. account fake, uso improprio):`, "") || "";
    }
    if (!confirm(`Confermi di voler ${action} l'account di ${selectedUser.user.first_name} ${selectedUser.user.last_name}?`)) return;
    try {
      await api.post(`/admin/users/${selectedUser.user.id}/block`, { blocked: !isBlocked, reason });
      await openUserDetail(selectedUser.user.id);
      loadUsers(userSearch);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    const email = selectedUser.user.email;
    if (!confirm(`⚠️ ATTENZIONE: stai per ELIMINARE DEFINITIVAMENTE l'account di ${selectedUser.user.first_name} ${selectedUser.user.last_name} (${email}).\n\nGli appuntamenti futuri saranno annullati. Questa azione è irreversibile.\n\nContinuare?`)) return;
    const confirmEmail = prompt(`Per conferma, scrivi l'email dell'assistito da eliminare:\n\n${email}`);
    if (confirmEmail !== email) {
      alert("Email non coincide. Eliminazione annullata.");
      return;
    }
    try {
      await api.delete(`/admin/users/${selectedUser.user.id}`);
      setSelectedUser(null);
      loadUsers(userSearch);
      alert(`Account ${email} eliminato.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const saveUserEdit = async () => {
    if (!editUserForm) return;
    try {
      await api.patch(`/admin/users/${editUserForm.id}`, editUserForm);
      setEditUserForm(null);
      await openUserDetail(editUserForm.id);
      loadUsers(userSearch);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const [adminBookServices, setAdminBookServices] = useState([]);
  const [adminBookSlots, setAdminBookSlots] = useState([]);
  const startAdminBooking = async (userForBook) => {
    // Load services if not loaded
    if (adminBookServices.length === 0) {
      const r = await api.get("/services");
      setAdminBookServices(r.data.services);
    }
    setAdminBookForm({
      user_id: userForBook.id,
      user_name: `${userForBook.first_name} ${userForBook.last_name}`,
      service_id: "",
      staff_id: "",
      slot_date: "",
      slot_time: "",
      is_tesserato: false,
      notes: "",
    });
    setAdminBookSlots([]);
  };

  const loadAdminBookSlots = async (form) => {
    if (!form.service_id || !form.staff_id || !form.slot_date) { setAdminBookSlots([]); return; }
    try {
      const r = await api.get("/availability", { params: { service_id: form.service_id, staff_id: form.staff_id, day: form.slot_date }});
      setAdminBookSlots(r.data.slots || []);
    } catch { setAdminBookSlots([]); }
  };

  const submitAdminBooking = async () => {
    if (!adminBookForm) return;
    try {
      await api.post("/admin/bookings", {
        user_id: adminBookForm.user_id,
        service_id: adminBookForm.service_id,
        staff_id: adminBookForm.staff_id,
        slot_date: adminBookForm.slot_date,
        slot_time: adminBookForm.slot_time,
        is_tesserato: adminBookForm.is_tesserato,
        notes: adminBookForm.notes,
      });
      alert(`Appuntamento creato per ${adminBookForm.user_name}!`);
      setAdminBookForm(null);
      await openUserDetail(adminBookForm.user_id);
      loadBookings();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore prenotazione");
    }
  };

  const submitNewUser = async () => {
    if (!newUserForm) return;
    if (!newUserForm.first_name || !newUserForm.last_name || !newUserForm.phone) {
      alert("Nome, cognome e telefono sono obbligatori");
      return;
    }
    try {
      const r = await api.post("/admin/users", newUserForm);
      alert(`✅ Assistito ${r.data.user.first_name} ${r.data.user.last_name} registrato!\n\nEmail (per login): ${r.data.user.email}\nPassword temporanea: ${r.data.temp_password}\n\nComunica queste credenziali all'assistito.`);
      setNewUserForm(null);
      loadUsers(userSearch);
    } catch (e) {
      alert(e.response?.data?.detail || "Errore registrazione");
    }
  };

  const loadFeedback = async () => {
    try {
      const r = await api.get("/admin/feedback");
      setFeedbackData(r.data);
    } catch {}
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bookings");
      setBookings(res.data.bookings || []);
    } catch { nav("/admin"); }
    finally { setLoading(false); }
  };

  const loadNews = async () => {
    const r = await api.get("/news");
    setNews(r.data.news || []);
  };

  const loadNoShowStats = async () => {
    try {
      const r = await api.get("/admin/no-show-stats");
      setNoShowList(r.data.users || []);
    } catch {}
  };

  const logout = () => { clearToken(); localStorage.removeItem("labor_user"); nav("/admin"); };

  const updateStatus = async (id, status) => {
    if (status === "assente" && !confirm("Segnare questo assistito come ASSENTE? Riceverà un'email di richiamo.")) return;
    await api.patch(`/admin/bookings/${id}`, null, { params: { status } });
    loadBookings();
    loadNoShowStats();
  };

  const publishNews = async () => {
    if (!newNews.title || !newNews.body) return;
    await api.post("/admin/news", newNews);
    setNewNews({ title: "", body: "", pinned: false });
    loadNews();
  };

  const deleteNews = async (id) => {
    if (!confirm("Eliminare questa comunicazione?")) return;
    await api.delete(`/admin/news/${id}`);
    loadNews();
  };

  const downloadExport = async (format) => {
    const token = getToken();
    const res = await fetch(`${API}/admin/bookings/export.${format}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert("Errore export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `appuntamenti_labor.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const in7 = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); })();

  const [staffFilter, setStaffFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");

  const filtered = bookings.filter((b) => {
    if (staffFilter !== "all" && b.staff_id !== staffFilter) return false;
    if (serviceFilter !== "all" && b.service_id !== serviceFilter) return false;
    if (filter === "today") return b.slot_date === today;
    if (filter === "tomorrow") return b.slot_date === tomorrow;
    if (filter === "week") return b.slot_date >= today && b.slot_date <= in7 && b.status !== "annullato";
    if (filter === "upcoming") return b.slot_date >= today && b.status !== "annullato" && b.status !== "assente";
    if (filter === "past") return b.slot_date < today;
    if (filter === "no_show") return b.status === "assente";
    if (filter === "all") return true;
    return true;
  });

  // Distinct staff and services for filter dropdowns
  const distinctStaff = Array.from(new Set(bookings.map((b) => b.staff_id))).filter(Boolean);
  const distinctServices = Array.from(new Map(bookings.map((b) => [b.service_id, b.service_name])).entries());

  const stats = {
    today: bookings.filter((b) => b.slot_date === today && b.status !== "annullato").length,
    week: bookings.filter((b) => {
      const w = new Date(); w.setDate(w.getDate() + 7);
      return b.slot_date >= today && b.slot_date <= w.toISOString().slice(0, 10) && b.status !== "annullato";
    }).length,
    total: bookings.filter((b) => b.status !== "annullato").length,
    no_show: bookings.filter((b) => b.status === "assente").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-labor-muted">Dashboard</div>
          <h1 className="font-display text-4xl">Ciao {user?.name} 👋</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/" className="text-sm text-labor-muted hover:text-labor-ink">Vai al sito</Link>
          <button data-testid="admin-logout" onClick={logout} className="border border-labor-border px-4 py-2 flex items-center gap-2 hover:bg-labor-ink hover:text-white text-sm">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="paper-card p-5" data-testid="stat-today">
          <div className="text-xs uppercase tracking-widest text-labor-muted">Oggi</div>
          <div className="font-display text-4xl mt-1">{stats.today}</div>
          <div className="text-sm text-labor-muted">appuntamenti</div>
        </div>
        <div className="paper-card p-5" data-testid="stat-week">
          <div className="text-xs uppercase tracking-widest text-labor-muted">Prossimi 7 giorni</div>
          <div className="font-display text-4xl mt-1">{stats.week}</div>
          <div className="text-sm text-labor-muted">appuntamenti</div>
        </div>
        <div className="paper-card p-5" data-testid="stat-total">
          <div className="text-xs uppercase tracking-widest text-labor-muted">Totale attivi</div>
          <div className="font-display text-4xl mt-1">{stats.total}</div>
          <div className="text-sm text-labor-muted">non annullati</div>
        </div>
        <div className="paper-card p-5" data-testid="stat-no-show">
          <div className="text-xs uppercase tracking-widest text-labor-terracotta">Bidoni totali</div>
          <div className="font-display text-4xl mt-1 text-labor-terracotta">{stats.no_show}</div>
          <div className="text-sm text-labor-muted">assenze registrate</div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-1 mb-6 border-b border-labor-border flex-wrap">
        {[["bookings","Agenda Appuntamenti"],["calendar","Calendario"],["users","Assistiti"],["doc_requests","Richieste documenti"],["fallbacks","Domande senza risposta"],["revenue","Incassi"],["closures","Chiusure"],["tickets","Ticket Urgenza"],["kb","Knowledge Base"],["packages","Pacchetti"],["no_show","Bidoni ricorrenti"],["news","Newsletter CAF Labor"],["feedback","Feedback"],["settings","Account"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-5 py-3 font-medium ${tab === k ? "border-b-2 border-labor-terracotta text-labor-ink" : "text-labor-muted"}`}>{l}</button>
        ))}
      </div>

      {tab === "bookings" && (
        <>
          <div className="flex justify-between items-start gap-3 mb-4 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              {[["today","Oggi"],["tomorrow","Domani"],["week","Prossimi 7 gg"],["upcoming","Tutti i prossimi"],["past","Passati"],["no_show","Assenti"],["all","Tutti"]].map(([k,l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`} className={`px-4 py-2 text-sm font-medium ${filter === k ? "bg-labor-olive text-white" : "border border-labor-border hover:bg-labor-bg"}`}>{l}</button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              <select data-testid="filter-staff" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="border border-labor-border px-3 py-2 text-sm">
                <option value="all">Tutti gli operatori</option>
                {distinctStaff.map((s) => <option key={s} value={s}>{s === "vito" ? "Vito" : s === "avvocato" ? "Avvocato" : s}</option>)}
              </select>
              <select data-testid="filter-service" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="border border-labor-border px-3 py-2 text-sm max-w-[200px]">
                <option value="all">Tutti i servizi</option>
                {distinctServices.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
              <button onClick={() => downloadExport("csv")} data-testid="export-csv" className="border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-2 text-sm flex items-center gap-1">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button onClick={() => downloadExport("pdf")} data-testid="export-pdf" className="border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-2 text-sm flex items-center gap-1">
                <Download className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>
          <div className="text-sm text-labor-muted mb-3" data-testid="filtered-count">
            {filtered.length} appuntament{filtered.length === 1 ? "o" : "i"} trovat{filtered.length === 1 ? "o" : "i"}
          </div>
          {loading ? <div>Caricamento…</div> : filtered.length === 0 ? (
            <div className="paper-card p-10 text-center text-labor-muted">Nessun appuntamento in questa vista.</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((b) => (
                <div key={b.id} data-testid={`admin-booking-${b.id}`} className={`paper-card p-5 grid md:grid-cols-12 gap-4 items-center ${b.status === "annullato" || b.status === "assente" ? "opacity-70" : ""}`}>
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 text-labor-terracotta font-display text-2xl"><Calendar className="w-5 h-5" /> {b.slot_date}</div>
                    <div className="flex items-center gap-2 text-labor-ink font-medium mt-1"><Clock className="w-4 h-4" /> {b.slot_time}</div>
                    {b.created_at && <div className="text-[10px] text-labor-muted mt-1">Prenotato: {new Date(b.created_at).toLocaleString("it-IT", {day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"})}</div>}
                  </div>
                  <div className="md:col-span-4">
                    <div className="font-display text-lg flex items-center gap-2 flex-wrap">
                      {b.first_name} {b.last_name}
                      {b.is_tesserato && <span className="text-xs bg-labor-warning text-white px-1.5 py-0.5">TESSERATO</span>}
                      {b.user_id ? <span className="text-[10px] bg-labor-italianGreen text-white px-1.5 py-0.5" title="Utente registrato">🔒 ACCOUNT</span> : <span className="text-[10px] bg-labor-muted text-white px-1.5 py-0.5" title="Prenotazione da guest (pre-obbligo login)">GUEST</span>}
                    </div>
                    <a href={`tel:${b.phone}`} className="text-sm text-labor-olive hover:underline flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {b.phone || "—"}</a>
                    <a href={`mailto:${b.email}`} className="text-sm text-labor-muted hover:text-labor-olive flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {b.email}</a>
                  </div>
                  <div className="md:col-span-3">
                    <div className="text-sm flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {b.service_name}</div>
                    <div className="text-sm text-labor-muted flex items-center gap-1"><User className="w-3.5 h-3.5" /> {b.staff_name}</div>
                    {b.notes && <div className="text-xs text-labor-muted mt-1 italic">"{b.notes}"</div>}
                  </div>
                  <div className="md:col-span-3 flex flex-wrap gap-2 justify-end">
                    <span className={`text-xs px-2 py-1 font-semibold ${
                      b.status === "confermato" ? "bg-labor-olive text-white" :
                      b.status === "completato" ? "bg-labor-italianGreen text-white" :
                      b.status === "assente" ? "bg-labor-terracotta text-white" :
                      "bg-labor-border text-labor-muted"
                    }`}>{b.status.toUpperCase()}</span>
                    {b.status === "confermato" && (
                      <>
                        <button onClick={() => updateStatus(b.id, "completato")} data-testid={`btn-complete-${b.id}`} className="text-xs px-2 py-1 border border-labor-italianGreen text-labor-italianGreen hover:bg-labor-italianGreen hover:text-white flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Fatto</button>
                        <button onClick={() => updateStatus(b.id, "assente")} data-testid={`btn-noshow-${b.id}`} className="text-xs px-2 py-1 border border-labor-terracotta text-labor-terracotta hover:bg-labor-terracotta hover:text-white flex items-center gap-1"><UserX className="w-3.5 h-3.5" /> Bidone</button>
                        <button onClick={() => updateStatus(b.id, "annullato")} data-testid={`btn-cancel-${b.id}`} className="text-xs px-2 py-1 border border-labor-muted text-labor-muted hover:bg-labor-muted hover:text-white flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Annulla</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "calendar" && (
        <div>
          <div className="paper-card p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev" onClick={() => setCalendarMonth(({y,m}) => m===0 ? {y:y-1,m:11} : {y,m:m-1})} className="px-3 py-2 border border-labor-border hover:bg-labor-bg">‹</button>
              <div className="font-display text-2xl px-3">{["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"][calendarMonth.m]} {calendarMonth.y}</div>
              <button data-testid="cal-next" onClick={() => setCalendarMonth(({y,m}) => m===11 ? {y:y+1,m:0} : {y,m:m+1})} className="px-3 py-2 border border-labor-border hover:bg-labor-bg">›</button>
              <button onClick={() => { const d=new Date(); setCalendarMonth({y:d.getFullYear(),m:d.getMonth()}); }} className="ml-2 px-3 py-2 text-sm border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white">Oggi</button>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1"><span className="w-3 h-3 bg-labor-olive inline-block"></span> Confermato</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 bg-labor-terracotta inline-block"></span> Assente</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 bg-labor-italianGreen inline-block"></span> Completato</div>
            </div>
          </div>
          {(() => {
            const firstDay = new Date(calendarMonth.y, calendarMonth.m, 1);
            const lastDay = new Date(calendarMonth.y, calendarMonth.m + 1, 0);
            const startOffset = (firstDay.getDay() + 6) % 7; // Monday=0
            const daysInMonth = lastDay.getDate();
            const cells = [];
            for (let i = 0; i < startOffset; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            while (cells.length % 7 !== 0) cells.push(null);
            const dateStr = (d) => `${calendarMonth.y}-${String(calendarMonth.m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const todayStr = new Date().toISOString().slice(0,10);
            return (
              <div className="paper-card p-3">
                <div className="grid grid-cols-7 gap-1 mb-2 text-xs uppercase tracking-widest text-labor-muted text-center">
                  {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map((d) => <div key={d} className="py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((d, i) => {
                    if (d === null) return <div key={i} className="min-h-[80px]"></div>;
                    const ds = dateStr(d);
                    const dayBookings = bookings.filter((b) => b.slot_date === ds && b.status !== "annullato");
                    const isToday = ds === todayStr;
                    const isSelected = calendarSelectedDay === ds;
                    return (
                      <button
                        key={i}
                        data-testid={`cal-day-${ds}`}
                        onClick={() => setCalendarSelectedDay(ds)}
                        className={`min-h-[80px] p-2 border text-left hover:bg-labor-bg ${isSelected ? "border-labor-terracotta border-2" : isToday ? "border-labor-olive bg-labor-skyBg" : "border-labor-border"}`}
                      >
                        <div className={`text-sm font-semibold ${isToday ? "text-labor-olive" : ""}`}>{d}</div>
                        {dayBookings.length > 0 && (
                          <>
                            <div className="text-xs text-labor-muted mt-1">{dayBookings.length} app.</div>
                            <div className="flex gap-0.5 mt-1 flex-wrap">
                              {dayBookings.slice(0, 5).map((b, idx) => (
                                <span key={idx} className={`w-1.5 h-1.5 inline-block ${b.status === "confermato" ? "bg-labor-olive" : b.status === "assente" ? "bg-labor-terracotta" : b.status === "completato" ? "bg-labor-italianGreen" : "bg-labor-muted"}`}></span>
                              ))}
                            </div>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {calendarSelectedDay && (
            <div className="paper-card p-5 mt-4">
              <h3 className="font-display text-xl mb-3">Appuntamenti del {new Date(calendarSelectedDay).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
              {(() => {
                const dayB = bookings.filter((b) => b.slot_date === calendarSelectedDay).sort((a,b) => a.slot_time.localeCompare(b.slot_time));
                if (dayB.length === 0) return <div className="text-labor-muted text-sm">Nessun appuntamento in questa data.</div>;
                return (
                  <div className="space-y-2">
                    {dayB.map((b) => (
                      <div key={b.id} className={`paper-card p-3 grid grid-cols-4 gap-2 items-center ${b.status === "annullato" || b.status === "assente" ? "opacity-60" : ""}`} data-testid={`cal-booking-${b.id}`}>
                        <div className="font-display text-xl text-labor-terracotta">{b.slot_time}</div>
                        <div>
                          <div className="font-medium">{b.first_name} {b.last_name}</div>
                          <div className="text-xs text-labor-muted"><a href={`tel:${b.phone}`}>{b.phone}</a></div>
                        </div>
                        <div className="text-sm">{b.service_name}</div>
                        <div className="text-right"><span className={`text-xs px-2 py-1 ${b.status === "confermato" ? "bg-labor-olive text-white" : b.status === "assente" ? "bg-labor-terracotta text-white" : b.status === "completato" ? "bg-labor-italianGreen text-white" : "bg-labor-border text-labor-muted"}`}>{b.status.toUpperCase()}</span></div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <div>
          <div className="paper-card p-5 mb-4 flex gap-3 items-center flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <input
                data-testid="users-search"
                type="text"
                placeholder="Cerca per nome, cognome, email o telefono…"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); loadUsers(e.target.value); }}
                className="w-full px-3 py-2.5 border border-labor-border"
              />
            </div>
            <button
              data-testid="btn-new-user"
              onClick={() => setNewUserForm({ first_name: "", last_name: "", phone: "", email: "", labor_points_bonus: 20 })}
              className="olive-btn px-4 py-2.5 text-sm font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Registra nuovo assistito
            </button>
            <div className="text-sm text-labor-muted">{users.length} totali</div>
          </div>
          {users.length === 0 ? (
            <div className="paper-card p-10 text-center text-labor-muted">
              Nessun assistito registrato ancora.<br/>
              <span className="text-xs">Non appena qualcuno si registra su caflaborbitontoapp.online lo vedrai qui.</span>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {users.map((u) => (
                <div key={u.id} data-testid={`admin-user-${u.id}`} className={`paper-card p-5 hover:border-b-labor-olive cursor-pointer ${u.blocked ? "border-l-4 border-l-red-600" : ""}`} onClick={() => openUserDetail(u.id)}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg truncate flex items-center gap-2">
                        {u.first_name} {u.last_name}
                        {u.blocked && <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5">🔒 BLOCCATO</span>}
                        {u.auth_provider === "google" && <span className="text-[10px] bg-white border border-labor-border px-1.5 py-0.5">GOOGLE</span>}
                      </div>
                      <div className="text-sm text-labor-muted flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5 flex-shrink-0" /> {u.email}</div>
                      {u.phone && <div className="text-sm text-labor-muted flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {u.phone}</div>}
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-display text-2xl text-labor-terracotta">{u.bookings_count}</div>
                      <div className="text-labor-muted">appuntamenti</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-labor-border text-xs">
                    <div>
                      <div className="text-labor-muted">Free</div>
                      <div className="font-semibold">{u.free_questions ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-labor-muted">Labor P.</div>
                      <div className="font-semibold text-labor-olive">{u.labor_points ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-labor-muted">Acquisti</div>
                      <div className="font-semibold">€{(u.purchased_eur || 0).toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-labor-muted">Bidoni</div>
                      <div className={`font-semibold ${u.no_show_count > 0 ? "text-labor-terracotta" : ""}`}>{u.no_show_count || 0}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {selectedUser && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
              <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="user-detail-modal">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-labor-muted">Assistito</div>
                    <h2 className="font-display text-3xl flex items-center gap-2 flex-wrap">
                      {selectedUser.user.first_name} {selectedUser.user.last_name}
                      {selectedUser.user.blocked && <span className="text-xs bg-red-600 text-white px-2 py-1 rounded">BLOCCATO</span>}
                    </h2>
                    <div className="text-sm text-labor-muted mt-1">{selectedUser.user.email} · {selectedUser.user.phone || "no telefono"}</div>
                    {selectedUser.user.blocked && selectedUser.user.blocked_reason && (
                      <div className="text-xs text-red-700 mt-1 italic">Motivo blocco: {selectedUser.user.blocked_reason}</div>
                    )}
                  </div>
                  <button onClick={() => setSelectedUser(null)} className="text-labor-muted hover:text-labor-ink"><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  <button
                    data-testid="btn-edit-user"
                    onClick={() => setEditUserForm({
                      id: selectedUser.user.id,
                      first_name: selectedUser.user.first_name,
                      last_name: selectedUser.user.last_name,
                      email: selectedUser.user.email,
                      phone: selectedUser.user.phone || "",
                    })}
                    className="px-4 py-2 text-sm font-semibold flex items-center gap-2 border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white"
                  >
                    <FileText className="w-4 h-4" /> Modifica scheda
                  </button>
                  <button
                    data-testid="btn-admin-book-for-user"
                    onClick={() => startAdminBooking(selectedUser.user)}
                    className="px-4 py-2 text-sm font-semibold flex items-center gap-2 bg-labor-terracotta text-white hover:opacity-90"
                  >
                    <Calendar className="w-4 h-4" /> Prenota per lui
                  </button>
                  <button
                    data-testid="btn-block-user"
                    onClick={toggleBlock}
                    className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 ${selectedUser.user.blocked ? "bg-labor-italianGreen text-white hover:opacity-90" : "border border-labor-warning text-labor-warning hover:bg-labor-warning hover:text-white"}`}
                  >
                    {selectedUser.user.blocked ? <><CheckCircle2 className="w-4 h-4" /> Sblocca account</> : <><UserX className="w-4 h-4" /> Blocca account</>}
                  </button>
                  <button
                    data-testid="btn-delete-user"
                    onClick={deleteUser}
                    className="px-4 py-2 text-sm font-semibold flex items-center gap-2 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" /> Elimina
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="paper-card p-3 text-center">
                    <div className="text-xs text-labor-muted">Free rimaste</div>
                    <div className="font-display text-2xl">{selectedUser.user.free_questions ?? 0}</div>
                  </div>
                  <div className="paper-card p-3 text-center">
                    <div className="text-xs text-labor-muted">Labor Point</div>
                    <div className="font-display text-2xl text-labor-olive">{selectedUser.user.labor_points ?? 0}</div>
                  </div>
                  <div className="paper-card p-3 text-center">
                    <div className="text-xs text-labor-muted">Iscrizione</div>
                    <div className="text-sm font-medium">{new Date(selectedUser.user.created_at).toLocaleDateString("it-IT")}</div>
                  </div>
                </div>

                {/* Ricarica manuale Labor Point */}
                <div className="paper-card p-4 mb-6 bg-labor-skyBg" data-testid="admin-adjust-lp-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Coins className="w-5 h-5 text-labor-olive" />
                    <h3 className="font-display text-lg">Ricarica Labor Point</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 items-end">
                    <div className="flex gap-2">
                      {[10, 25, 50, 100, 200].map((n) => (
                        <button
                          key={n}
                          data-testid={`quick-add-${n}`}
                          onClick={() => adjustLP(n, `Ricarica manuale +${n} LP`)}
                          className="olive-btn px-3 py-2 text-sm font-semibold"
                        >
                          +{n}
                        </button>
                      ))}
                    </div>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const fd = new FormData(e.target);
                        const amount = parseInt(fd.get("amount"));
                        const reason = fd.get("reason") || "";
                        if (!isNaN(amount) && amount !== 0) adjustLP(amount, reason);
                        e.target.reset();
                      }}
                      className="flex gap-2"
                    >
                      <input name="amount" data-testid="custom-amount" type="number" placeholder="±punti" className="flex-1 px-3 py-2 border border-labor-border text-sm" />
                      <input name="reason" data-testid="custom-reason" type="text" placeholder="motivo (opz.)" className="flex-1 px-3 py-2 border border-labor-border text-sm" />
                      <button type="submit" data-testid="custom-submit" className="border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-2 text-sm font-semibold">Applica</button>
                    </form>
                  </div>
                  <div className="text-xs text-labor-muted mt-2">
                    Usa numeri negativi per scalare punti. Ogni operazione viene registrata nello storico dell'assistito.
                  </div>
                </div>

                <AdminUserAgenda userId={selectedUser.user.id} />

                {/* CONDIVISIONE PDF */}
                <div className="paper-card p-4 mb-6" data-testid="admin-user-docs-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-labor-terracotta" />
                    <h3 className="font-display text-lg">Documenti condivisi</h3>
                    <span className="text-xs text-labor-muted">(auto-eliminati dopo 3 giorni)</span>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center mb-3">
                    <input
                      data-testid="user-doc-file"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      className="text-sm flex-1"
                    />
                    <button
                      data-testid="user-doc-upload"
                      onClick={uploadUserDoc}
                      disabled={!docFile || docUploading}
                      className="olive-btn px-4 py-2 text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
                    >
                      {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Condividi PDF
                    </button>
                  </div>
                  {userDocs.length === 0 ? (
                    <div className="text-xs text-labor-muted">Nessun documento condiviso.</div>
                  ) : (
                    <div className="space-y-2">
                      {userDocs.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-2 border border-labor-border p-2 text-sm">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate" title={d.filename}>{d.filename}</div>
                            <div className="text-xs text-labor-muted">
                              {(d.size_bytes / 1024).toFixed(0)} KB · scade il {new Date(d.expires_at).toLocaleDateString("it-IT")}
                            </div>
                          </div>
                          <button
                            data-testid={`doc-download-${d.id}`}
                            onClick={() => downloadAdminDoc(d.id, d.filename)}
                            className="border border-labor-olive text-labor-olive hover:bg-labor-olive hover:text-white px-3 py-1 text-xs"
                          >
                            <Download className="w-3 h-3 inline mr-1" /> Scarica
                          </button>
                          <button
                            data-testid={`doc-delete-${d.id}`}
                            onClick={() => deleteUserDoc(d.id)}
                            className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-3 py-1 text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <h3 className="font-display text-xl mb-2">Appuntamenti ({selectedUser.bookings.length})</h3>
                {selectedUser.bookings.length === 0 ? (
                  <div className="text-labor-muted text-sm">Nessun appuntamento.</div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {selectedUser.bookings.map((b) => (
                      <div key={b.id} className="paper-card p-3 flex justify-between items-center text-sm">
                        <div>
                          <div className="font-medium">{b.slot_date} · {b.slot_time}</div>
                          <div className="text-labor-muted text-xs">{b.service_name} · {b.staff_name}</div>
                        </div>
                        <span className={`text-xs px-2 py-1 ${b.status === "confermato" ? "bg-labor-olive text-white" : b.status === "assente" ? "bg-labor-terracotta text-white" : b.status === "completato" ? "bg-labor-italianGreen text-white" : "bg-labor-border text-labor-muted"}`}>{b.status.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MODALE MODIFICA SCHEDA */}
          {editUserForm && (
            <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setEditUserForm(null)}>
              <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-display text-2xl">Modifica scheda</h3>
                  <button onClick={() => setEditUserForm(null)}><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="space-y-3">
                  <label className="block text-sm">Nome
                    <input data-testid="edit-first-name" className="w-full mt-1 px-3 py-2 border border-labor-border" value={editUserForm.first_name} onChange={(e) => setEditUserForm({...editUserForm, first_name: e.target.value})} />
                  </label>
                  <label className="block text-sm">Cognome
                    <input data-testid="edit-last-name" className="w-full mt-1 px-3 py-2 border border-labor-border" value={editUserForm.last_name} onChange={(e) => setEditUserForm({...editUserForm, last_name: e.target.value})} />
                  </label>
                  <label className="block text-sm">Telefono
                    <input data-testid="edit-phone" className="w-full mt-1 px-3 py-2 border border-labor-border" value={editUserForm.phone} onChange={(e) => setEditUserForm({...editUserForm, phone: e.target.value})} />
                  </label>
                  <label className="block text-sm">Email
                    <input data-testid="edit-email" type="email" className="w-full mt-1 px-3 py-2 border border-labor-border" value={editUserForm.email} onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})} />
                  </label>
                </div>
                <div className="flex gap-2 mt-5 justify-end">
                  <button onClick={() => setEditUserForm(null)} className="px-4 py-2 text-sm border border-labor-border">Annulla</button>
                  <button data-testid="edit-save" onClick={saveUserEdit} className="olive-btn px-4 py-2 text-sm font-semibold flex items-center gap-2"><Save className="w-4 h-4" /> Salva</button>
                </div>
              </div>
            </div>
          )}

          {/* MODALE PRENOTA PER CONTO DI */}
          {adminBookForm && (
            <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setAdminBookForm(null)}>
              <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-book-modal">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-display text-2xl">Prenota per l'assistito</h3>
                    <div className="text-sm text-labor-muted">{adminBookForm.user_name}</div>
                  </div>
                  <button onClick={() => setAdminBookForm(null)}><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="space-y-3">
                  <label className="block text-sm">Servizio
                    <select
                      data-testid="admin-book-service"
                      className="w-full mt-1 px-3 py-2 border border-labor-border"
                      value={adminBookForm.service_id}
                      onChange={(e) => {
                        const f = {...adminBookForm, service_id: e.target.value, slot_time: ""};
                        setAdminBookForm(f);
                        loadAdminBookSlots(f);
                      }}
                    >
                      <option value="">-- scegli --</option>
                      {adminBookServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </label>
                  <label className="block text-sm">Operatore
                    <select
                      data-testid="admin-book-staff"
                      className="w-full mt-1 px-3 py-2 border border-labor-border"
                      value={adminBookForm.staff_id}
                      onChange={(e) => {
                        const f = {...adminBookForm, staff_id: e.target.value, slot_time: ""};
                        setAdminBookForm(f);
                        loadAdminBookSlots(f);
                      }}
                    >
                      <option value="">-- scegli --</option>
                      <option value="vito">Vito</option>
                      <option value="avvocato">Avvocato</option>
                    </select>
                  </label>
                  <label className="block text-sm">Data
                    <input
                      data-testid="admin-book-date"
                      type="date"
                      className="w-full mt-1 px-3 py-2 border border-labor-border"
                      value={adminBookForm.slot_date}
                      min={new Date().toISOString().slice(0,10)}
                      onChange={(e) => {
                        const f = {...adminBookForm, slot_date: e.target.value, slot_time: ""};
                        setAdminBookForm(f);
                        loadAdminBookSlots(f);
                      }}
                    />
                  </label>
                  {adminBookForm.slot_date && (
                    <div className="text-sm">
                      <div className="mb-2">Orari disponibili:</div>
                      {adminBookSlots.length === 0 ? (
                        <div className="text-labor-muted text-xs">Nessuno slot disponibile per questa combinazione.</div>
                      ) : (
                        <div className="grid grid-cols-4 gap-2">
                          {adminBookSlots.map((t) => (
                            <button
                              key={t}
                              data-testid={`admin-slot-${t}`}
                              onClick={() => setAdminBookForm({...adminBookForm, slot_time: t})}
                              className={`px-2 py-2 text-sm border ${adminBookForm.slot_time === t ? "bg-labor-terracotta text-white border-labor-terracotta" : "border-labor-border hover:border-labor-terracotta"}`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <label className="block text-sm">Note (opzionale)
                    <textarea data-testid="admin-book-notes" rows={2} className="w-full mt-1 px-3 py-2 border border-labor-border" value={adminBookForm.notes} onChange={(e) => setAdminBookForm({...adminBookForm, notes: e.target.value})} />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={adminBookForm.is_tesserato} onChange={(e) => setAdminBookForm({...adminBookForm, is_tesserato: e.target.checked})} /> Servizio riservato ai tesserati
                  </label>
                </div>
                <div className="flex gap-2 mt-5 justify-end">
                  <button onClick={() => setAdminBookForm(null)} className="px-4 py-2 text-sm border border-labor-border">Annulla</button>
                  <button
                    data-testid="admin-book-confirm"
                    disabled={!adminBookForm.service_id || !adminBookForm.staff_id || !adminBookForm.slot_date || !adminBookForm.slot_time}
                    onClick={submitAdminBooking}
                    className="olive-btn px-4 py-2 text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Prenota
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* MODALE REGISTRA NUOVO ASSISTITO */}
          {newUserForm && (
            <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setNewUserForm(null)}>
              <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="new-user-modal">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-display text-2xl">Registra nuovo assistito</h3>
                    <p className="text-xs text-labor-muted mt-1">Per assistiti che si presentano di persona (es. anziani senza smartphone).</p>
                  </div>
                  <button onClick={() => setNewUserForm(null)}><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="new-first-name" placeholder="Nome *" value={newUserForm.first_name} onChange={(e) => setNewUserForm({...newUserForm, first_name: e.target.value})} className="px-3 py-2 border border-labor-border" />
                    <input data-testid="new-last-name" placeholder="Cognome *" value={newUserForm.last_name} onChange={(e) => setNewUserForm({...newUserForm, last_name: e.target.value})} className="px-3 py-2 border border-labor-border" />
                  </div>
                  <input data-testid="new-phone" placeholder="Telefono *" value={newUserForm.phone} onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})} className="w-full px-3 py-2 border border-labor-border" />
                  <input data-testid="new-email" type="email" placeholder="Email (opzionale, se manca ne creo una fittizia)" value={newUserForm.email} onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})} className="w-full px-3 py-2 border border-labor-border" />
                  <label className="block text-sm">Labor Point omaggio
                    <input data-testid="new-lp" type="number" min="0" value={newUserForm.labor_points_bonus} onChange={(e) => setNewUserForm({...newUserForm, labor_points_bonus: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2 border border-labor-border" />
                  </label>
                  <div className="text-xs text-labor-muted bg-labor-skyBg p-3 border-l-4 border-labor-olive">
                    ℹ️ Verrà creato un account con password temporanea, che dovrai comunicare all'assistito (o al familiare). L'assistito può poi impostare un PIN semplice per accedere.
                  </div>
                </div>
                <div className="flex gap-2 mt-5 justify-end">
                  <button onClick={() => setNewUserForm(null)} className="px-4 py-2 text-sm border border-labor-border">Annulla</button>
                  <button data-testid="new-user-submit" onClick={submitNewUser} className="olive-btn px-4 py-2 text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Crea assistito</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "revenue" && (
        <div>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="paper-card p-6 bg-gradient-to-br from-labor-italianGreen to-green-700 text-white">
              <div className="text-xs uppercase tracking-widest opacity-90">Incassi Stripe totali</div>
              <div className="font-display text-4xl mt-2" data-testid="stripe-total-eur">€{(revenue?.stripe?.total_eur || 0).toFixed(2)}</div>
              <div className="text-xs opacity-90 mt-1">{revenue?.stripe?.count || 0} transazioni · {revenue?.stripe?.total_lp || 0} LP venduti</div>
            </div>
            <div className="paper-card p-6 bg-gradient-to-br from-labor-terracotta to-red-700 text-white">
              <div className="text-xs uppercase tracking-widest opacity-90">Labor Point regalati</div>
              <div className="font-display text-4xl mt-2">{revenue?.admin_gifts?.total_lp || 0} LP</div>
              <div className="text-xs opacity-90 mt-1">{revenue?.admin_gifts?.count || 0} ricariche manuali</div>
            </div>
            <div className="paper-card p-6 bg-gradient-to-br from-labor-olive to-labor-oliveLight text-white">
              <div className="text-xs uppercase tracking-widest opacity-90">Incasso mese corrente</div>
              <div className="font-display text-4xl mt-2">€{((revenue?.monthly || []).find((m) => m._id === new Date().toISOString().slice(0,7))?.eur || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="paper-card p-5">
            <h3 className="font-display text-xl mb-3">Incassi per mese</h3>
            {(revenue?.monthly || []).length === 0 ? (
              <div className="text-labor-muted text-sm">Nessun incasso Stripe ancora. Quando un assistito compra un pacchetto, apparirà qui.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-labor-muted border-b border-labor-border">
                  <tr><th className="text-left py-2">Mese</th><th className="text-right">Incasso</th><th className="text-right">LP</th><th className="text-right">N. pagamenti</th></tr>
                </thead>
                <tbody>
                  {revenue.monthly.map((m) => (
                    <tr key={m._id} className="border-b border-labor-border">
                      <td className="py-2">{m._id}</td>
                      <td className="text-right font-display text-lg">€{m.eur.toFixed(2)}</td>
                      <td className="text-right">{m.lp}</td>
                      <td className="text-right">{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {revenue?.recent_payments?.length > 0 && (
            <div className="paper-card p-5 mt-4">
              <h3 className="font-display text-xl mb-3">Ultimi pagamenti Stripe</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {revenue.recent_payments.map((p) => (
                  <div key={p.id} className="flex justify-between items-center paper-card p-3 text-sm">
                    <div>
                      <div className="font-medium">{p.package_name}</div>
                      <div className="text-xs text-labor-muted">{new Date(p.created_at).toLocaleString("it-IT")}</div>
                    </div>
                    <div className="font-display text-lg text-labor-italianGreen">€{p.amount_eur?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "closures" && (
        <div className="max-w-2xl">
          <div className="paper-card p-5 mb-4">
            <h3 className="font-display text-xl mb-3">Aggiungi chiusura straordinaria</h3>
            <div className="grid sm:grid-cols-3 gap-2">
              <input data-testid="closure-date" type="date" min={new Date().toISOString().slice(0,10)} value={newClosure.date} onChange={(e) => setNewClosure({...newClosure, date: e.target.value})} className="px-3 py-2.5 border border-labor-border" />
              <input data-testid="closure-reason" placeholder="Motivo (opz.)" value={newClosure.reason} onChange={(e) => setNewClosure({...newClosure, reason: e.target.value})} className="px-3 py-2.5 border border-labor-border sm:col-span-1" />
              <button data-testid="closure-add" onClick={addClosure} className="olive-btn px-4 py-2.5 font-semibold flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Chiudi</button>
            </div>
            <div className="text-xs text-labor-muted mt-2">⚠️ Gli appuntamenti già presi in quella data verranno automaticamente annullati. Ricordati di chiamare gli assistiti coinvolti per riallinearli.</div>
          </div>
          <div className="paper-card p-5">
            <h3 className="font-display text-xl mb-3">Chiusure programmate ({closures.length})</h3>
            {closures.length === 0 ? (
              <div className="text-labor-muted text-sm">Nessuna chiusura programmata.</div>
            ) : (
              <div className="space-y-2">
                {closures.map((c) => (
                  <div key={c.id} className="flex justify-between items-center paper-card p-3" data-testid={`closure-${c.id}`}>
                    <div>
                      <div className="font-display text-lg">{new Date(c.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                      {c.reason && <div className="text-sm text-labor-muted">{c.reason}</div>}
                    </div>
                    <button onClick={() => removeClosure(c.id)} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "tickets" && (
        <div className="max-w-2xl">
          <div className="paper-card p-6 mb-4 bg-gradient-to-br from-labor-warning to-orange-500 text-white">
            <div className="flex items-center gap-3 mb-3">
              <Ticket className="w-8 h-8" />
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Ticket Urgenza · oggi ({ticketToday?.date})</div>
                <div className="font-display text-4xl mt-1" data-testid="admin-tickets-remaining">
                  {ticketToday ? Math.max(0, ticketToday.total_available - ticketToday.used) : "…"} / {ticketToday?.total_available || 0}
                </div>
                <div className="text-sm opacity-90">disponibili · {ticketToday?.used || 0} utilizzati</div>
              </div>
            </div>
          </div>
          {ticketToday && (
            <>
              <div className="paper-card p-5 mb-4">
                <div className="font-display text-lg mb-3">Modifica utilizzati</div>
                <div className="flex items-center gap-3">
                  <button data-testid="ticket-used-minus" onClick={() => updateTicket({ used: Math.max(0, ticketToday.used - 1) })} className="px-4 py-2 border border-labor-border hover:bg-labor-ink hover:text-white"><Minus className="w-4 h-4" /></button>
                  <div className="font-display text-3xl w-16 text-center">{ticketToday.used}</div>
                  <button data-testid="ticket-used-plus" onClick={() => updateTicket({ used: ticketToday.used + 1 })} className="px-4 py-2 border border-labor-border hover:bg-labor-ink hover:text-white"><Plus className="w-4 h-4" /></button>
                  <div className="text-sm text-labor-muted ml-3">Registra ogni ticket consegnato in sede.</div>
                </div>
              </div>
              <div className="paper-card p-5">
                <div className="font-display text-lg mb-3">Modifica disponibilità totale di oggi</div>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="ticket-total-input"
                    type="number" min="0"
                    defaultValue={ticketToday.total_available}
                    className="w-24 px-3 py-2 border border-labor-border text-center font-display text-2xl"
                    onBlur={(e) => {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v) && v !== ticketToday.total_available) updateTicket({ total_available: v });
                    }}
                  />
                  <div className="flex gap-2">
                    {[0, 3, 5, 10].map((n) => (
                      <button key={n} onClick={() => updateTicket({ total_available: n })} className="px-3 py-2 border border-labor-border text-sm hover:bg-labor-olive hover:text-white">
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-labor-muted mt-3">Il numero mostrato agli assistiti nell'app.</div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "kb" && (
        <div>
          {/* IMPORT ZIP */}
          <div className="paper-card p-5 mb-6 bg-labor-skyBg" data-testid="kb-import-panel">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-labor-terracotta" />
              <h3 className="font-display text-xl">Importa da archivio ZIP di chat WhatsApp</h3>
            </div>
            <p className="text-sm text-labor-muted mb-3">
              Carica un file ZIP contenente export di chat WhatsApp (.txt). Il sistema estrae le domande/risposte, <strong>anonimizza automaticamente</strong> telefoni, email, codici fiscali e IBAN, e le importa nella Knowledge Base.
            </p>
            <div className="flex gap-2 items-center flex-wrap">
              <input data-testid="kb-zip-file" type="file" accept=".zip" onChange={(e) => setKbZipFile(e.target.files?.[0] || null)} className="text-sm" />
              <button data-testid="kb-zip-upload" onClick={uploadKbZip} disabled={!kbZipFile || kbZipUploading} className="olive-btn px-4 py-2 text-sm font-semibold disabled:opacity-40 flex items-center gap-2">
                {kbZipUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Importa
              </button>
            </div>
            <div className="text-xs text-labor-muted mt-2">Max 20MB · Solo .txt dentro il ZIP · Max 50 voci per file</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
          <div className="paper-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-labor-olive" />
              <h2 className="font-display text-2xl">Nuova voce Knowledge Base</h2>
            </div>
            <div className="space-y-3">
              <input data-testid="kb-question" placeholder="Domanda (es. Che documenti servono per l'ISEE?)" value={newKb.question} onChange={(e) => setNewKb({...newKb, question: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border" />
              <textarea data-testid="kb-answer" placeholder="Risposta verificata (chiara, breve)" rows={5} value={newKb.answer} onChange={(e) => setNewKb({...newKb, answer: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border" />
              <input data-testid="kb-keywords" placeholder="Parole chiave (es. isee, dsu, documenti)" value={newKb.keywords} onChange={(e) => setNewKb({...newKb, keywords: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border" />
              <select data-testid="kb-category" value={newKb.category} onChange={(e) => setNewKb({...newKb, category: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border">
                {["generale","730","isee","pensioni","invalidita","naspi","successione","cie","legale"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button data-testid="kb-add" onClick={addKb} className="olive-btn px-5 py-2.5 font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Aggiungi</button>
              <p className="text-xs text-labor-muted">Labor AI userà queste risposte verificate come priorità quando la domanda è pertinente.</p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="text-sm text-labor-muted">
                Voci in Knowledge Base: {kbEntries.length}
                {kbSelected.size > 0 && <span className="ml-2 text-labor-terracotta font-semibold">· {kbSelected.size} selezionate</span>}
              </div>
              <div className="flex gap-2 flex-wrap">
                {kbEntries.length > 0 && (
                  <button
                    data-testid="kb-select-all"
                    onClick={toggleKbSelectAll}
                    className="text-xs border border-labor-border px-2 py-1 hover:bg-labor-skyBg"
                  >
                    {kbSelected.size === kbEntries.length ? "Deseleziona tutto" : "Seleziona tutto"}
                  </button>
                )}
                {kbSelected.size > 0 && (
                  <button
                    data-testid="kb-bulk-delete"
                    onClick={bulkDeleteKb}
                    className="text-xs bg-labor-warning text-white px-2 py-1 hover:opacity-90 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Elimina selezionate ({kbSelected.size})
                  </button>
                )}
                {kbEntries.length > 0 && (
                  <button
                    data-testid="kb-delete-all"
                    onClick={deleteAllKb}
                    className="text-xs border border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-2 py-1"
                  >
                    Elimina TUTTA la KB
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {kbEntries.length === 0 ? (
                <div className="paper-card p-5 text-center text-labor-muted text-sm">Nessuna voce ancora. Aggiungi la prima FAQ.</div>
              ) : kbEntries.map((e) => (
                <div key={e.id} className={`paper-card p-4 ${kbSelected.has(e.id) ? "border-l-4 border-labor-terracotta bg-labor-skyBg" : ""}`} data-testid={`kb-entry-${e.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <input
                      type="checkbox"
                      data-testid={`kb-check-${e.id}`}
                      checked={kbSelected.has(e.id)}
                      onChange={() => toggleKbSelected(e.id)}
                      className="mt-1 w-4 h-4 accent-labor-terracotta flex-shrink-0 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="text-xs bg-labor-skyBg text-labor-ink px-2 py-0.5 inline-block">{e.category}</div>
                      <div className="font-medium mt-1">{e.question}</div>
                      <div className="text-sm text-labor-muted mt-1 line-clamp-3">{e.answer}</div>
                      {e.keywords && <div className="text-xs text-labor-muted mt-1 italic">🔑 {e.keywords}</div>}
                    </div>
                    <button data-testid={`kb-delete-${e.id}`} onClick={() => deleteKb(e.id)} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div>
          <div className="paper-card p-5 mb-6">
            <div className="flex items-center gap-2 mb-3"><Package className="w-5 h-5 text-labor-terracotta" /><h2 className="font-display text-2xl">Nuovo pacchetto</h2></div>
            <div className="grid sm:grid-cols-5 gap-3">
              <input data-testid="pkg-name" placeholder="Nome (es. Base)" value={newPkg.name} onChange={(e) => setNewPkg({...newPkg, name: e.target.value})} className="px-3 py-2.5 border border-labor-border" />
              <input data-testid="pkg-price" type="number" step="0.01" placeholder="Prezzo €" value={newPkg.price_eur} onChange={(e) => setNewPkg({...newPkg, price_eur: parseFloat(e.target.value)})} className="px-3 py-2.5 border border-labor-border" />
              <input data-testid="pkg-points" type="number" placeholder="Labor Point" value={newPkg.labor_points} onChange={(e) => setNewPkg({...newPkg, labor_points: parseInt(e.target.value)})} className="px-3 py-2.5 border border-labor-border" />
              <input data-testid="pkg-order" type="number" placeholder="Ordine" value={newPkg.order} onChange={(e) => setNewPkg({...newPkg, order: parseInt(e.target.value)})} className="px-3 py-2.5 border border-labor-border" />
              <button data-testid="pkg-add" onClick={addPackage} className="olive-btn px-5 py-2.5 font-semibold flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Aggiungi</button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.map((p) => (
              <div key={p.id} className={`paper-card p-5 ${!p.active ? "opacity-50" : ""}`} data-testid={`admin-pkg-${p.id}`}>
                <div className="text-xs uppercase tracking-widest text-labor-muted">{p.name}</div>
                <input type="text" defaultValue={p.name} onBlur={(e) => e.target.value !== p.name && updatePackage({ ...p, name: e.target.value })} className="w-full font-display text-xl mt-1 bg-transparent border-b border-labor-border" />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <label className="text-xs">Prezzo €<input type="number" step="0.01" defaultValue={p.price_eur} onBlur={(e) => { const v = parseFloat(e.target.value); if (v !== p.price_eur) updatePackage({ ...p, price_eur: v }); }} className="w-full mt-1 px-2 py-1.5 border border-labor-border" /></label>
                  <label className="text-xs">Labor Point<input type="number" defaultValue={p.labor_points} onBlur={(e) => { const v = parseInt(e.target.value); if (v !== p.labor_points) updatePackage({ ...p, labor_points: v }); }} className="w-full mt-1 px-2 py-1.5 border border-labor-border" /></label>
                </div>
                <div className="flex justify-between items-center mt-3">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.active} onChange={() => updatePackage({ ...p, active: !p.active })} /> Attivo</label>
                  <button onClick={() => deletePackage(p.id)} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "no_show" && (
        <div>
          <p className="text-labor-muted mb-4">Assistiti che hanno mancato uno o più appuntamenti senza avvisare. Le email di richiamo vengono inviate automaticamente quando li segni come "Bidone".</p>
          {noShowList.length === 0 ? (
            <div className="paper-card p-10 text-center text-labor-muted">Nessun bidone registrato. 🎉</div>
          ) : (
            <div className="space-y-2">
              {noShowList.map((u) => (
                <div key={u.email} data-testid={`no-show-${u.email}`} className="paper-card p-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-lg">{u.first_name} {u.last_name}</div>
                    <div className="text-sm text-labor-muted flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {u.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {u.phone}</span>
                      <span>Ultima assenza: {u.last_date}</span>
                    </div>
                  </div>
                  <div className={`px-3 py-1.5 font-semibold text-white text-sm ${u.count >= 3 ? "bg-labor-terracotta" : u.count === 2 ? "bg-labor-warning" : "bg-labor-muted"}`}>
                    {u.count} bidone{u.count > 1 ? "i" : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "feedback" && (
        <div>
          <div className="paper-card p-6 mb-6 text-white" style={{ background: "linear-gradient(135deg, #003D82 0%, #0055B8 100%)" }}>
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Feedback totali</div>
                <div className="font-display text-5xl mt-1 text-white font-bold" data-testid="fb-total">{feedbackData.total}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Voto medio</div>
                <div className="font-display text-5xl mt-1 flex items-center gap-2 text-white font-bold" data-testid="fb-avg">
                  {feedbackData.avg_rating || "—"} {feedbackData.avg_rating > 0 && <Star className="w-8 h-8 fill-labor-warning text-labor-warning" />}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest opacity-90">Ultimo feedback</div>
                <div className="text-sm mt-1 text-white">{feedbackData.feedback[0]?.created_at?.slice(0, 10) || "—"}</div>
              </div>
            </div>
          </div>
          {feedbackData.feedback.length === 0 ? (
            <div className="paper-card p-10 text-center text-labor-muted" data-testid="fb-empty">Nessun feedback ricevuto ancora. Aspetta che gli assistiti lo lascino dal profilo.</div>
          ) : (
            <div className="space-y-3">
              {feedbackData.feedback.map((f) => (
                <div key={f.id} data-testid={`fb-item-${f.id}`} className="paper-card p-5">
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {[1,2,3,4,5].map(n => (<Star key={n} className={`w-4 h-4 ${n <= f.rating ? "fill-labor-warning text-labor-warning" : "text-labor-border"}`} />))}
                        <span className="text-xs bg-labor-skyBg text-labor-ink px-2 py-0.5 ml-1">{f.category}</span>
                      </div>
                      <div className="font-medium">{f.user_name || "Utente anonimo"} <span className="text-labor-muted text-sm font-normal">· {f.user_email || "no email"}</span></div>
                      {f.text && <p className="text-sm text-labor-muted mt-2 italic">"{f.text}"</p>}
                    </div>
                    <div className="text-xs text-labor-muted">{new Date(f.created_at).toLocaleDateString("it-IT")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "bookings" && <AdminWaitlist />}

      {tab === "news" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="paper-card p-6">
            <h2 className="font-display text-2xl mb-1">Nuova Newsletter CAF Labor</h2>
            <p className="text-xs text-labor-muted mb-4" data-testid="news-push-note">Alla pubblicazione viene inviata una notifica push a tutti gli assistiti che hanno attivato le notifiche.</p>
            <div className="space-y-3">
              <input data-testid="news-title" placeholder="Titolo" value={newNews.title} onChange={(e) => setNewNews({...newNews, title: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border" />
              <textarea data-testid="news-body" placeholder="Testo…" rows={6} value={newNews.body} onChange={(e) => setNewNews({...newNews, body: e.target.value})} className="w-full px-3 py-2.5 border border-labor-border" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newNews.pinned} onChange={(e) => setNewNews({...newNews, pinned: e.target.checked})} /> Fissa in alto (importante)</label>
              <button data-testid="news-publish" onClick={publishNews} className="olive-btn px-5 py-2.5 font-semibold">Pubblica</button>
            </div>
          </div>
          <div className="space-y-3">
            {news.map((n) => (
              <div key={n.id} className="paper-card p-5" data-testid={`admin-news-${n.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-labor-muted mb-1">
                      {n.pinned && <Pin className="w-3.5 h-3.5 text-labor-terracotta" />}
                      {new Date(n.created_at).toLocaleDateString("it-IT")}
                    </div>
                    <div className="font-display text-lg">{n.title}</div>
                    <p className="text-sm text-labor-muted mt-1 whitespace-pre-line">{n.body.slice(0, 160)}{n.body.length > 160 ? "…" : ""}</p>
                  </div>
                  <button onClick={() => deleteNews(n.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "doc_requests" && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-2xl">Richieste documenti dagli assistiti</h2>
            <div className="flex gap-1 flex-wrap">
              {[
                ["all", `Tutte (${docRequests.length})`],
                ["richiesto", `Nuove (${docReqCounts.richiesto || 0})`],
                ["in_lavorazione", `In lavorazione (${docReqCounts.in_lavorazione || 0})`],
                ["pronto", `Pronte (${docReqCounts.pronto || 0})`],
                ["annullato", `Annullate (${docReqCounts.annullato || 0})`],
              ].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`doc-req-filter-${k}`}
                  onClick={() => setDocReqFilter(k)}
                  className={`px-3 py-1 text-xs font-medium border ${docReqFilter === k ? "bg-labor-terracotta text-white border-labor-terracotta" : "border-labor-border hover:bg-labor-skyBg"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const list = docReqFilter === "all" ? docRequests : docRequests.filter((r) => r.status === docReqFilter);
            if (list.length === 0) return <div className="paper-card p-10 text-center text-labor-muted text-sm">Nessuna richiesta in questa categoria.</div>;
            return (
              <div className="space-y-3">
                {list.map((r) => (
                  <div key={r.id} className="paper-card p-5" data-testid={`admin-doc-req-${r.id}`}>
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`text-xs px-2 py-0.5 font-semibold ${
                            r.status === "pronto" ? "bg-labor-italianGreen text-white" :
                            r.status === "in_lavorazione" ? "bg-labor-terracotta text-white" :
                            r.status === "annullato" ? "bg-red-600 text-white" :
                            "bg-labor-warning text-white"
                          }`}>
                            {r.status === "in_lavorazione" ? "IN LAVORAZIONE" : r.status.toUpperCase()}
                          </span>
                          <span className="text-xs text-labor-muted">{new Date(r.created_at).toLocaleString("it-IT")}</span>
                        </div>
                        <div className="font-display text-lg">{r.doc_type}</div>
                        <div className="text-sm text-labor-muted mt-0.5">
                          <strong>{r.user_name}</strong> · <a href={`mailto:${r.user_email}`} className="hover:text-labor-olive">{r.user_email}</a>
                          {r.user_phone && <> · <a href={`tel:${r.user_phone}`} className="hover:text-labor-olive">{r.user_phone}</a></>}
                        </div>
                        <div className="mt-2 text-sm whitespace-pre-wrap">{r.description}</div>
                        {r.attachment_id && <div className="mt-2"><AttachmentThumb attId={r.attachment_id} mime={r.attachment_mime} filename={r.attachment_filename} testId={`admin-doc-req-thumb-${r.id}`} /></div>}
                        {r.notes && <div className="text-xs text-labor-muted italic mt-1">Note: {r.notes}</div>}
                        {r.admin_note && <div className="mt-2 border-l-2 border-labor-olive pl-2 text-xs bg-labor-skyBg p-2">Mia risposta: {r.admin_note}</div>}
                      </div>
                      <button
                        data-testid={`admin-doc-req-delete-${r.id}`}
                        onClick={() => deleteDocRequest(r.id)}
                        className="text-red-600 hover:bg-red-50 p-1.5 flex-shrink-0"
                        title="Elimina richiesta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-labor-border">
                      <input
                        data-testid={`admin-doc-req-note-${r.id}`}
                        type="text"
                        placeholder="Nota per l'assistito (opzionale)"
                        value={reqAdminNotes[r.id] ?? r.admin_note ?? ""}
                        onChange={(e) => setReqAdminNotes({...reqAdminNotes, [r.id]: e.target.value})}
                        className="w-full px-3 py-2 border border-labor-border text-sm mb-2"
                      />
                      <div className="flex gap-2 flex-wrap">
                        <button
                          data-testid={`admin-doc-req-inlav-${r.id}`}
                          onClick={() => updateDocRequest(r.id, "in_lavorazione")}
                          className="text-xs bg-labor-terracotta text-white px-3 py-1.5 hover:opacity-90"
                        >
                          Prendo in carico
                        </button>
                        <button
                          data-testid={`admin-doc-req-pronto-${r.id}`}
                          onClick={() => updateDocRequest(r.id, "pronto")}
                          className="text-xs bg-labor-italianGreen text-white px-3 py-1.5 hover:opacity-90"
                        >
                          Segna come pronto
                        </button>
                        <button
                          data-testid={`admin-doc-req-open-user-${r.id}`}
                          onClick={() => openUserDetail(r.user_id)}
                          className="text-xs border border-labor-olive text-labor-olive px-3 py-1.5 hover:bg-labor-olive hover:text-white flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" /> Apri scheda per caricare PDF
                        </button>
                        <button
                          data-testid={`admin-doc-req-cancel-${r.id}`}
                          onClick={() => updateDocRequest(r.id, "annullato")}
                          className="text-xs border border-red-600 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {tab === "fallbacks" && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="font-display text-2xl">Domande senza risposta</h2>
              <p className="text-xs text-labor-muted mt-1">Quando l'IA non sa rispondere arriva qui. Chiama l'assistito o apri una scheda per prenotare un appuntamento al posto suo.</p>
            </div>
            <div className="flex gap-1 flex-wrap">
              {[
                ["all", `Tutte (${fallbacks.length})`],
                ["nuovo", `Nuove (${fallbackCounts.nuovo || 0})`],
                ["in_lavorazione", `In lavorazione (${fallbackCounts.in_lavorazione || 0})`],
                ["risolto", `Risolte (${fallbackCounts.risolto || 0})`],
                ["archiviato", `Archiviate (${fallbackCounts.archiviato || 0})`],
              ].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`fb-filter-${k}`}
                  onClick={() => setFallbackFilter(k)}
                  className={`px-3 py-1 text-xs font-medium border ${fallbackFilter === k ? "bg-labor-terracotta text-white border-labor-terracotta" : "border-labor-border hover:bg-labor-skyBg"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const list = fallbackFilter === "all" ? fallbacks : fallbacks.filter((a) => a.status === fallbackFilter);
            if (list.length === 0) return <div className="paper-card p-10 text-center text-labor-muted text-sm">L'IA sta rispondendo a tutto correttamente ✓</div>;
            return (
              <div className="space-y-3">
                {list.map((a) => (
                  <div key={a.id} className="paper-card p-5" data-testid={`fallback-${a.id}`}>
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`text-xs px-2 py-0.5 font-semibold ${
                            a.status === "risolto" ? "bg-labor-italianGreen text-white" :
                            a.status === "in_lavorazione" ? "bg-labor-terracotta text-white" :
                            a.status === "archiviato" ? "bg-labor-border text-labor-muted" :
                            "bg-labor-warning text-white animate-pulse"
                          }`}>{a.status.toUpperCase()}</span>
                          <span className="text-xs text-labor-muted">{new Date(a.created_at).toLocaleString("it-IT")}</span>
                          {a.has_attachment && !a.attachment_id && <span className="text-xs bg-labor-skyBg px-2 py-0.5">📎 {a.attachment_filename}</span>}
                        </div>
                        <div className="font-display text-base mb-1">"{a.question}"</div>
                        {a.attachment_id && <div className="mb-2"><AttachmentThumb attId={a.attachment_id} mime={a.attachment_mime} filename={a.attachment_filename} testId={`fb-thumb-${a.id}`} /></div>}
                        <div className="text-sm text-labor-muted">
                          <strong>{a.user_name || "Ospite"}</strong>
                          {a.user_email && <> · <a href={`mailto:${a.user_email}`} className="hover:text-labor-olive">{a.user_email}</a></>}
                          {a.user_phone && <> · <a href={`tel:${a.user_phone}`} className="hover:text-labor-olive">{a.user_phone}</a></>}
                        </div>
                        {a.resolution_note && <div className="mt-2 border-l-2 border-labor-olive pl-2 text-xs bg-labor-skyBg p-2">Nota: {a.resolution_note}</div>}
                      </div>
                      <button
                        data-testid={`fb-delete-${a.id}`}
                        onClick={() => deleteFallback(a.id)}
                        className="text-red-600 hover:bg-red-50 p-1.5 flex-shrink-0"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-labor-border">
                      <input
                        data-testid={`fb-note-${a.id}`}
                        type="text"
                        placeholder="Come l'hai risolto? (opzionale)"
                        value={fallbackNotes[a.id] ?? a.resolution_note ?? ""}
                        onChange={(e) => setFallbackNotes({...fallbackNotes, [a.id]: e.target.value})}
                        className="w-full px-3 py-2 border border-labor-border text-sm mb-2"
                      />
                      <div className="flex gap-2 flex-wrap">
                        {a.user_phone && (
                          <a
                            data-testid={`fb-call-${a.id}`}
                            href={`tel:${a.user_phone}`}
                            className="text-xs bg-labor-italianGreen text-white px-3 py-1.5 hover:opacity-90 flex items-center gap-1"
                          >📞 Chiama</a>
                        )}
                        <button
                          data-testid={`fb-inlav-${a.id}`}
                          onClick={() => updateFallback(a.id, "in_lavorazione")}
                          className="text-xs bg-labor-terracotta text-white px-3 py-1.5 hover:opacity-90"
                        >Preso in carico</button>
                        <button
                          data-testid={`fb-resolved-${a.id}`}
                          onClick={() => updateFallback(a.id, "risolto")}
                          className="text-xs bg-labor-italianGreen text-white px-3 py-1.5 hover:opacity-90"
                        >Segna come risolto</button>
                        <button
                          data-testid={`fb-archive-${a.id}`}
                          onClick={() => updateFallback(a.id, "archiviato")}
                          className="text-xs border border-labor-border text-labor-muted hover:bg-labor-skyBg px-3 py-1.5"
                        >Archivia</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-lg">
          <div className="paper-card p-6" data-testid="admin-settings">
            <h2 className="font-display text-2xl mb-2 flex items-center gap-2">
              <Lock className="w-5 h-5 text-labor-olive" /> Cambia password admin
            </h2>
            <p className="text-xs text-labor-muted mb-4">
              Requisiti: min 8 caratteri, almeno 1 numero e 1 lettera maiuscola. Applica anche a Vito e Marianna.
            </p>
            <div className="space-y-2">
              <input
                data-testid="admin-pw-old"
                type="password"
                placeholder="Password attuale"
                autoComplete="current-password"
                value={adminPwForm.old_password}
                onChange={(e) => setAdminPwForm({...adminPwForm, old_password: e.target.value})}
                className="w-full px-3 py-2 border border-labor-border text-sm"
              />
              <input
                data-testid="admin-pw-new"
                type="password"
                placeholder="Nuova password (min 8, 1 numero, 1 maiuscola)"
                autoComplete="new-password"
                value={adminPwForm.new_password}
                onChange={(e) => setAdminPwForm({...adminPwForm, new_password: e.target.value})}
                className="w-full px-3 py-2 border border-labor-border text-sm"
              />
              <input
                data-testid="admin-pw-new2"
                type="password"
                placeholder="Ripeti nuova password"
                autoComplete="new-password"
                value={adminPwForm.new_password2}
                onChange={(e) => setAdminPwForm({...adminPwForm, new_password2: e.target.value})}
                className="w-full px-3 py-2 border border-labor-border text-sm"
              />
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <button
                data-testid="admin-pw-submit"
                onClick={handleAdminChangePassword}
                disabled={adminPwSaving}
                className="olive-btn px-4 py-2 text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                {adminPwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Aggiorna password
              </button>
              {adminPwMsg && (
                <span data-testid="admin-pw-msg" className={`text-sm ${adminPwMsg.type === "ok" ? "text-labor-italianGreen" : "text-red-600"}`}>
                  {adminPwMsg.text}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
